import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { AlertOctagon, ChevronRight, Clock, Handshake, ListChecks, Inbox as InboxIcon, CalendarClock, Flame } from "lucide-react";
import { CommitmentBadge, RelationBadge } from "@/components/Badges";
import { formatDate, relTime } from "@/lib/utils-fos";

// -----------------------------------------------------------------------------
// NeedsAttention — one-scroll snapshot of everything urgent, aggregated from
// tasks, escalations, commitments, meetings, and the inbox. Each section
// uses the same card style as its native page so the visual language stays
// consistent — tap the section title (or any deep-link chevron) to jump to
// the full view.
// -----------------------------------------------------------------------------
export default function NeedsAttention() {
  const [tasks, setTasks] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [liveEvents, setLiveEvents] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [people, setPeople] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [ts, es, cs, ms, rs, pp, pr] = await Promise.all([
        endpoints.tasks(),
        endpoints.escalations(),
        endpoints.commitments(),
        endpoints.meetings(),
        endpoints.reminders(),
        endpoints.people(),
        endpoints.projects(),
      ]);
      setTasks(ts); setEscalations(es); setCommitments(cs);
      setMeetings(ms); setReminders(rs); setPeople(pp); setProjects(pr);
      // Google Calendar live events — silently ignore if not connected.
      try {
        const gc = await endpoints.nango.calendarEvents({ max_results: 20 });
        setLiveEvents(gc.events || []);
      } catch { setLiveEvents([]); }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const peopleById   = useMemo(() => Object.fromEntries(people.map((p) => [p.id, p])), [people]);
  const projectsById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const now = Date.now();

  // ── Slice each collection to just the urgent stuff ──
  const overdueTasks = useMemo(
    () => tasks
      .filter((t) => t.status !== "done" && t.due_date && new Date(t.due_date).getTime() < now)
      .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""))
      .slice(0, 8),
    [tasks, now],
  );

  const openEscalations = useMemo(
    () => escalations
      .filter((e) => (e.status || "pending") !== "resolved")
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
      .slice(0, 6),
    [escalations],
  );

  const urgentCommitments = useMemo(
    () => commitments
      .filter((c) => c.status === "broken" || c.status === "at_risk")
      .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""))
      .slice(0, 8),
    [commitments],
  );

  // Meetings in the next 24h: prefer live Google Calendar events, fall back
  // to the local meetings collection if calendar isn't connected.
  const upcomingMeetings = useMemo(() => {
    const in24h = now + 24 * 60 * 60 * 1000;
    if (liveEvents.length > 0) {
      return liveEvents
        .filter((e) => {
          const s = e.start_at && new Date(e.start_at).getTime();
          return s && s >= now && s <= in24h;
        })
        .slice(0, 6);
    }
    return meetings
      .filter((m) => {
        const s = m.start_at && new Date(m.start_at).getTime();
        return s && s >= now && s <= in24h;
      })
      .slice(0, 6);
  }, [liveEvents, meetings, now]);

  const dueReminders = useMemo(
    () => reminders
      .filter((r) => r.status !== "done" && r.remind_at && new Date(r.remind_at).getTime() <= now + 60 * 60 * 1000)
      .sort((a, b) => (a.remind_at || "").localeCompare(b.remind_at || ""))
      .slice(0, 6),
    [reminders, now],
  );

  const total = overdueTasks.length + openEscalations.length + urgentCommitments.length + upcomingMeetings.length + dueReminders.length;

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="needs-attention-page">
      <header>
        <h1 className="text-2xl font-semibold text-neutral-900 flex items-center gap-2">
          <AlertOctagon size={22} className="text-rose-500" /> Needs attention
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          {loading ? "Scanning your workspace…" : total === 0 ? "You're all caught up. Nothing urgent." : `${total} urgent item${total === 1 ? "" : "s"} across your workspace.`}
        </p>
      </header>

      {/* Escalations */}
      <Section
        title="Open escalations"
        icon={<Flame size={14} className="text-rose-500" />}
        deepLink="/activity"
        count={openEscalations.length}
        empty="No open escalations."
      >
        {openEscalations.map((e) => (
          <div key={e.id} className="fos-card p-3 border border-rose-100 bg-rose-50/40" data-testid={`attention-escalation-${e.id}`}>
            <p className="text-sm text-neutral-900 leading-snug">{e.title || e.summary || "Untitled escalation"}</p>
            <div className="mt-1 flex items-center gap-3 text-[11px] text-neutral-500">
              <span>Status: {e.status || "pending"}</span>
              {e.created_at && <span>Opened {relTime(e.created_at)}</span>}
              {projectsById[e.related_project_id] && <span>· {projectsById[e.related_project_id].name}</span>}
            </div>
          </div>
        ))}
      </Section>

      {/* Overdue tasks */}
      <Section
        title="Overdue tasks"
        icon={<ListChecks size={14} className="text-amber-600" />}
        deepLink="/projects"
        count={overdueTasks.length}
        empty="No overdue tasks."
      >
        {overdueTasks.map((t) => (
          <div key={t.id} className="fos-card p-3 border border-amber-100 bg-amber-50/40" data-testid={`attention-task-${t.id}`}>
            <p className="text-sm text-neutral-900 leading-snug">{t.title}</p>
            <div className="mt-1 flex items-center gap-3 text-[11px] text-neutral-500">
              <Clock size={10} className="text-amber-600" />
              <span>Due {formatDate(t.due_date)}</span>
              {projectsById[t.project_id] && <span>· {projectsById[t.project_id].name}</span>}
            </div>
          </div>
        ))}
      </Section>

      {/* Broken / at-risk commitments */}
      <Section
        title="Commitments at risk"
        icon={<Handshake size={14} className="text-rose-500" />}
        deepLink="/commitments"
        count={urgentCommitments.length}
        empty="Every commitment is on track."
      >
        {urgentCommitments.map((c) => (
          <div key={c.id} className="fos-card p-3 border border-neutral-200" data-testid={`attention-commitment-${c.id}`}>
            <p className="text-sm text-neutral-900 leading-snug">{c.what}</p>
            <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-neutral-500">
              {c.due_date && <span>Due {formatDate(c.due_date)}</span>}
              {peopleById[c.from_person_id] && (
                <span>From <RelationBadge person={peopleById[c.from_person_id]} /></span>
              )}
              <CommitmentBadge status={c.status} />
            </div>
          </div>
        ))}
      </Section>

      {/* Meetings in next 24h */}
      <Section
        title="Meetings in next 24h"
        icon={<CalendarClock size={14} className="text-emerald-600" />}
        deepLink="/calendar"
        count={upcomingMeetings.length}
        empty="Nothing on the calendar."
      >
        {upcomingMeetings.map((m) => (
          <div key={m.id} className="fos-card p-3 border border-emerald-100 bg-emerald-50/30" data-testid={`attention-meeting-${m.id}`}>
            <p className="text-sm text-neutral-900 leading-snug">{m.summary || m.title || "Meeting"}</p>
            <div className="mt-1 flex items-center gap-2 text-[11px] text-neutral-500">
              <Clock size={10} />
              <span>{m.start_at ? new Date(m.start_at).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "TBD"}</span>
              {m.location && <span>· {m.location}</span>}
            </div>
          </div>
        ))}
      </Section>

      {/* Reminders due now */}
      <Section
        title="Reminders due"
        icon={<InboxIcon size={14} className="text-neutral-500" />}
        deepLink="/calendar"
        count={dueReminders.length}
        empty="No reminders due right now."
      >
        {dueReminders.map((r) => (
          <div key={r.id} className="fos-card p-3 border border-neutral-200" data-testid={`attention-reminder-${r.id}`}>
            <p className="text-sm text-neutral-900 leading-snug">{r.title}</p>
            {r.remind_at && (
              <p className="text-[11px] text-neutral-500 mt-1">Due {relTime(r.remind_at)}</p>
            )}
          </div>
        ))}
      </Section>
    </div>
  );
}

function Section({ title, icon, deepLink, count, empty, children }) {
  return (
    <section data-testid={`attention-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="text-sm font-semibold text-neutral-900">{title}</h2>
          <span className="text-[11px] text-neutral-500">{count}</span>
        </div>
        <Link to={deepLink} className="text-[11px] text-neutral-500 hover:text-neutral-800 flex items-center gap-0.5">
          Open <ChevronRight size={12} />
        </Link>
      </div>
      {count === 0 ? (
        <p className="text-xs text-neutral-400 italic pl-6">{empty}</p>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </section>
  );
}
